import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';

export default function ViewStaffModal({ open, staff, onClose }) {
  if (!staff) return null;

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Terminated':
        return 'error';
      case 'Resigned':
        return 'warning';
      case 'On Leave':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: '#2a2a2a',
          border: '1px solid #444',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#C4A574',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          Staff Details - {staff.FullName}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2, bgcolor: '#2a2a2a' }}>
        {/* Status Badge */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <Chip
            label={staff.Status || 'Active'}
            color={getStatusColor(staff.Status)}
            sx={{ fontSize: '1rem', fontWeight: 600, px: 2, py: 2.5 }}
          />
        </Box>

        <Grid container spacing={3}>
          {/* Personal Information */}
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{ color: '#C4A574', fontWeight: 600, mb: 2 }}
            >
              Personal Information
            </Typography>
            <Divider sx={{ mb: 2, borderColor: '#444' }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ color: '#ccc' }}>
              Full Name
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#fff' }}>
              {staff.FullName}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ color: '#ccc' }}>
              Email
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#fff' }}>
              {staff.Email || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ color: '#ccc' }}>
              Phone
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#fff' }}>
              {staff.Phone || staff.PhoneNumber || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ color: '#ccc' }}>
              Staff ID
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#fff' }}>
              {staff.StaffId}
            </Typography>
          </Grid>

          {/* Employment Information */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography
              variant="h6"
              sx={{ color: '#C4A574', fontWeight: 600, mb: 2 }}
            >
              Employment Information
            </Typography>
            <Divider sx={{ mb: 2, borderColor: '#444' }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ color: '#ccc' }}>
              Designation
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#fff' }}>
              {staff.Designation || 'Not Assigned'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ color: '#ccc' }}>
              Staff Type
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#fff' }}>
              {staff.StaffType || 'Task-Based'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ color: '#ccc' }}>
              Monthly Salary
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#4CAF50' }}>
              ₹{staff.MonthlySalary?.toLocaleString()}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ color: '#ccc' }}>
              Joining Date
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#fff' }}>
              {formatDate(staff.JoiningDate)}
            </Typography>
          </Grid>

          {/* Termination Information (if applicable) */}
          {(staff.Status === 'Terminated' || staff.Status === 'Resigned') && (
            <>
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography
                  variant="h6"
                  sx={{ color: '#f44336', fontWeight: 600, mb: 2 }}
                >
                  {staff.Status === 'Terminated' ? 'Termination' : 'Resignation'} Information
                </Typography>
                <Divider sx={{ mb: 2, borderColor: '#444' }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: '#ccc' }}>
                  Last Working Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: '#fff' }}>
                  {formatDate(staff.LastWorkingDate)}
                </Typography>
              </Grid>

              {staff.TerminationReason && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ color: '#ccc' }}>
                    {staff.Status === 'Terminated' ? 'Termination' : 'Resignation'} Reason
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#fff' }}>
                    {staff.TerminationReason}
                  </Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#1a1a1a', borderTop: '1px solid #444' }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            bgcolor: '#C4A574',
            color: 'white',
            '&:hover': { bgcolor: '#B39562' },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
